/**
 * 星球活动的两套服务实现（并进通讯器模块的 communicator 服务，见 ./module 与 shared/services/types 的 CommunicatorService）。
 * - live：GET /events（免登录）；/event-tells 一律带当前宠物的 pet_id（GET 列表、POST {event_id}、DELETE /event-tells/{event_id}；CSRF 由 client 带）。
 *   契约都挂了 response_model，照契约类型取回。
 * - 演示：几个明确的演示活动（标题带“（演示）”，页面另挂“演示活动”标识），“告诉 TA / 不告诉了”在本机内存里按宠物记，
 *   拒绝的规则照后端（web_events/tells.py）：活动没开放 404 event_not_open、TA 去不了那里 409 destination_unavailable、
 *   已经去了撤不回 409 tell_already_used、没告诉过撤回 404 tell_not_found；再点一次“告诉 TA”原样答那一条，撤回也是幂等的。
 */
import type { PlanetEvent, PlanetEventList, PlanetEventTell, PlanetEventTellList, PlanetEventTellRequest } from "@/shared/contracts";
import type { ApiClient } from "@/shared/api/client";
import { ApiError } from "@/shared/api/errors";
import type { CommunicatorService } from "@/shared/services/types";
import { delay } from "@/fixtures/world";

/** 通讯器服务里星球活动那四个方法（接口定义在 shared/services/types 的 CommunicatorService，这里只取这一部分）。 */
export type PlanetEventService = Pick<CommunicatorService, "planetEvents" | "eventTells" | "tellEvent" | "withdrawEventTell">;

export function livePlanetEventService(api: ApiClient): PlanetEventService {
  return {
    planetEvents: (signal) => api.request<PlanetEventList>("/events", { signal }),
    eventTells: (petId, signal) => api.request<PlanetEventTellList>("/event-tells", { query: { pet_id: petId }, signal }),
    tellEvent: (petId, eventId) =>
      api.request<PlanetEventTell>("/event-tells", { method: "POST", query: { pet_id: petId }, body: { event_id: eventId } satisfies PlanetEventTellRequest }),
    // 不带 source 就是家人那一行（后端缺省 owner）；撤 TA 自己听说的那一行带 ?source=heard
    withdrawEventTell: (petId, eventId, source) =>
      api.request<PlanetEventTell>(`/event-tells/${encodeURIComponent(eventId)}`, { method: "DELETE", query: source ? { pet_id: petId, source } : { pet_id: petId } }),
  };
}

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

/** 演示活动（相对“现在”排时间窗，任何时候打开都是开放中的）。地点都是演示世界里已有的示意地名。 */
export function demoPlanetEvents(now: number): PlanetEvent[] {
  const at = (ms: number) => new Date(now + ms).toISOString();
  return [
    {
      event_id: "fx-event-lantern",
      title: "中秋社区灯会（演示）",
      story: "码头边挂起了一排纸灯笼，居民们带着自己做的小灯来。\n想看看的，傍晚过去走走就好。",
      destination_key: "fx-dest-central-pier",
      destination_title: "中环码头（示意）",
      venue_name: "海滨长廊（演示场地）",
      window_start: at(-1 * DAY),
      window_end: at(3 * DAY),
      fare_note: "车费由星球活动补助",
    },
    {
      event_id: "fx-event-beach",
      title: "清晨海边捡拾（演示）",
      story: "天刚亮的时候，大家在海边慢慢走，顺手把沙滩上的小垃圾捡起来。",
      destination_key: "fx-dest-shenzhen-bay",
      destination_title: "深圳湾（示意）",
      venue_name: null,
      window_start: at(1 * DAY),
      window_end: at(1 * DAY + 4 * HOUR),
      fare_note: "车费由星球活动补助",
    },
    {
      event_id: "fx-event-music",
      title: "街角小音乐会（演示）",
      story: "广场上有居民弹琴唱歌，路过的可以坐下来听一会儿。",
      destination_key: "fx-dest-downtown",
      destination_title: "香港市区（示意）",
      venue_name: "街角广场（演示场地）",
      window_start: at(-2 * DAY),
      window_end: at(1 * DAY),
      fare_note: "车费由星球活动补助",
    },
    {
      event_id: "fx-event-sunrise",
      title: "山顶看日出（演示）",
      story: "天没亮就上山，等太阳从海面上出来。",
      destination_key: "fx-dest-peak",
      destination_title: "山顶（示意）",
      venue_name: null,
      window_start: at(2 * DAY),
      window_end: at(2 * DAY + 3 * HOUR),
      fare_note: "车费由星球活动补助",
    },
  ];
}

/** 演示里 TA 此刻去不了的地方（告诉 TA 时照后端答 409 destination_unavailable）。 */
const DEMO_UNREACHABLE = new Set(["fx-dest-peak"]);

/** 每只宠物一开始的演示记录：清晨海边捡拾家人告诉过了、TA 自己也听说了（D10）；街角小音乐会 TA 自己去了；已经结束的社区画展 TA 也去过；另有一条作废的（页面不列）。 */
function demoSeed(petId: string, now: number, events: PlanetEvent[]): PlanetEventTell[] {
  const at = (ms: number) => new Date(now + ms).toISOString();
  const of = (id: string) => events.find((event) => event.event_id === id)!;
  const from = (event: PlanetEvent, status: PlanetEventTell["status"], toldAgo: number, journeyId: string | null = null): PlanetEventTell => ({
    event_id: event.event_id,
    pet_id: petId,
    title: event.title,
    destination_key: event.destination_key,
    window_start: event.window_start,
    window_end: event.window_end,
    status,
    told_at: at(-toldAgo),
    journey_id: journeyId,
  });
  return [
    from(of("fx-event-beach"), "active", 2 * HOUR),
    { ...from(of("fx-event-beach"), "active", 5 * HOUR), source: "heard" },
    from(of("fx-event-music"), "used", 3 * DAY, "fx-journey-event-music"),
    {
      event_id: "fx-event-gallery",
      pet_id: petId,
      title: "社区小画展（演示）",
      destination_key: "fx-dest-downtown",
      window_start: at(-9 * DAY),
      window_end: at(-6 * DAY),
      status: "used",
      told_at: at(-10 * DAY),
      journey_id: "fx-journey-event-gallery",
    },
    {
      event_id: "fx-event-kite",
      pet_id: petId,
      title: "海边放风筝（演示）",
      destination_key: "fx-dest-shenzhen-bay",
      window_start: at(-5 * DAY),
      window_end: at(-4 * DAY),
      status: "void",
      told_at: at(-6 * DAY),
      journey_id: null,
    },
  ];
}

const refuse = (status: number, code: ApiError["code"], reason: string, message: string) =>
  new ApiError({ kind: "http", status, code, message, retryable: false, details: { reason } });

/** 演示服务：本机内存、按宠物记；规则照后端（见文件头）。 */
export function demoPlanetEventService(clock: () => number = () => Date.now()): PlanetEventService {
  // 时间窗从整点排（页面上是“18:00”这样的整点，不是随打开时刻的零碎分钟）。
  const started = Math.floor(clock() / HOUR) * HOUR;
  const events = demoPlanetEvents(started);
  // 按“活动｜来源”记：一个活动可能有家人告诉的、TA 自己听说的两行（D10）
  const keyOf = (eventId: string, source: "owner" | "heard") => `${eventId}|${source}`;
  const byPet = new Map<string, Map<string, PlanetEventTell>>();
  const tellsOf = (petId: string) => {
    let tells = byPet.get(petId);
    if (!tells) {
      tells = new Map(demoSeed(petId, started, events).map((tell) => [keyOf(tell.event_id, tell.source === "heard" ? "heard" : "owner"), tell]));
      byPet.set(petId, tells);
    }
    return tells;
  };
  return {
    planetEvents: async () => delay({ events: events.map((event) => ({ ...event })), as_of: new Date(clock()).toISOString() }),
    eventTells: async (petId) => delay({ tells: [...tellsOf(petId).values()].map((tell) => ({ ...tell })) }),
    tellEvent: async (petId, eventId) => {
      const event = events.find((candidate) => candidate.event_id === eventId);
      if (!event) throw refuse(404, "NOT_FOUND", "event_not_open", "这个活动现在没有开放。");
      const tells = tellsOf(petId);
      const existing = tells.get(keyOf(eventId, "owner"));
      if (existing?.status === "active" || existing?.status === "used") return delay({ ...existing });
      if (DEMO_UNREACHABLE.has(event.destination_key)) throw refuse(409, "CONFLICT", "destination_unavailable", "TA 现在去不了那里。");
      const tell: PlanetEventTell = {
        event_id: event.event_id,
        pet_id: petId,
        title: event.title,
        destination_key: event.destination_key,
        window_start: event.window_start,
        window_end: event.window_end,
        status: "active",
        told_at: new Date(clock()).toISOString(),
        journey_id: null,
      };
      tells.set(keyOf(eventId, "owner"), tell);
      return delay({ ...tell });
    },
    withdrawEventTell: async (petId, eventId, source = "owner") => {
      const tells = tellsOf(petId);
      const existing = tells.get(keyOf(eventId, source));
      if (!existing) throw refuse(404, "NOT_FOUND", "tell_not_found", "还没有告诉过 TA 这个活动。");
      if (existing.status === "used") throw refuse(409, "CONFLICT", "tell_already_used", "TA 已经去过这个活动了。");
      const withdrawn: PlanetEventTell = { ...existing, status: "withdrawn" };
      tells.set(keyOf(eventId, source), withdrawn);
      return delay({ ...withdrawn });
    },
  };
}
